import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Download, ChevronDown } from 'lucide-react';
import { useProviderDownload } from '@/hooks/useProviderDownload';

interface ProviderDownloadProps {
  providerId: number;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  className?: string;
}

export function ProviderDownload({ 
  providerId, 
  variant = 'outline', 
  size = 'sm',
  className = ''
}: ProviderDownloadProps) {
  const {
    canDownload,
    isSingleRegion,
    isMultipleRegions,
    regionsData,
    handleDownload,
    isExporting,
    isLoading
  } = useProviderDownload(providerId);

  if (isLoading) {
    return (
      <Button 
        variant={variant} 
        size={size} 
        disabled 
        className={className}
      >
        <Download className="h-4 w-4 mr-2" />
        Loading...
      </Button>
    );
  }

  if (!canDownload) {
    return (
      <Button 
        variant={variant} 
        size={size} 
        disabled 
        className={className}
        title="No published versions available"
      >
        <Download className="h-4 w-4 mr-2" />
        Download
      </Button>
    );
  }

  // Single region - direct download button
  if (isSingleRegion) {
    return (
      <Button 
        variant={variant} 
        size={size} 
        onClick={() => handleDownload()}
        disabled={isExporting}
        className={className}
        title={`Download ${regionsData[0]?.region_name} data`}
      >
        <Download className="h-4 w-4 mr-2" />
        {isExporting ? 'Exporting...' : 'Download'}
      </Button>
    );
  }

  // Multiple regions - dropdown selector
  if (isMultipleRegions) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant={variant} 
            size={size}
            disabled={isExporting}
            className={`${className} flex items-center gap-1`}
          >
            <Download className="h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Download'}
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[200px]">
          <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
            Select Region
          </div>
          <DropdownMenuSeparator />
          {regionsData.map((region) => (
            <DropdownMenuItem
              key={region.region_id}
              onClick={() => handleDownload(region.region_id)}
              className="flex items-center justify-between"
            >
              <span>{region.region_name}</span>
              <span className="text-xs text-muted-foreground">
                v{region.latest_published_version?.version_number}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return null;
}